import { useQuery } from '@tanstack/react-query'

import { consulta, pedir } from './nucleo'

/**
 * Los idiomas de una web y las versiones de un documento en ellos (CLAUDE.md §37). Lo decide el núcleo
 * (`src/idiomas/versiones.ts`); aquí solo se pinta. El español es el principal y no se apaga.
 */
export type CodigoDeIdioma = 'en' | 'es' | 'pt'

export type IdiomasDeLaWeb = {
  principal: CodigoDeIdioma
  /** Los encendidos, el principal el primero. */
  encendidos: CodigoDeIdioma[]
  disponibles: Array<{ codigo: CodigoDeIdioma; nombre: string; propio: string }>
  puedeCambiar: boolean
  /** Los que el plan de la cuenta deja encender (§37). Sin el dato (un núcleo anterior), todos. */
  delPlan?: Array<'en' | 'pt'>
  /** Si la web vende: qué versión de cada producto lleva las existencias (§37, I4). Las demás usan las suyas. */
  existencias: null | { idioma: CodigoDeIdioma }
}

/** Los componentes personalizados que se quedan en español al encender otro idioma, y cómo pedir que se arreglen. */
export type ComponentesEnEspanol = {
  componentes: Array<{ ruta: string; motivo: string; ejemplos: string[] }>
  arreglo: null | { alcance: 'sitio'; rutas: string[]; prompt: string; quedan: number }
}

/** Lo que lleva Diseño en el estado de la navegación cuando se viene a arreglar algo: la petición ya escrita. */
export type PeticionDeDiseno = { alcance: 'sitio'; rutas: string[]; prompt: string }

export type VersionEnIdioma = {
  idioma: CodigoDeIdioma
  nombre: string
  id: null | number
  titulo: null | string
  estado: 'borrador' | 'publicada' | null
  desactualizada: boolean
}

export type VersionesDeUnDocumento = { idioma: CodigoDeIdioma; versiones: VersionEnIdioma[]; desactualizada: boolean; varios: boolean }

/** Las colecciones que van por idioma: las mismas que en el núcleo (`COLECCIONES_POR_IDIOMA`). */
export const COLECCIONES_POR_IDIOMA = new Set(['paginas', 'entradas', 'productos', 'servicios', 'categorias', 'categorias-de-servicios', 'etiquetas', 'navegacion', 'contenidos', 'terminos', 'formularios'])

export const NOMBRE_DE_IDIOMA: Record<CodigoDeIdioma, string> = { es: 'Español', en: 'Inglés', pt: 'Portugués' }

export const useIdiomas = (sitio: null | number) =>
  useQuery({
    queryKey: ['idiomas', sitio],
    queryFn: () => pedir<IdiomasDeLaWeb>(`/panel/idiomas${consulta({ sitio: sitio ?? 0 })}`),
    enabled: sitio !== null,
    staleTime: 60_000,
  })

/** Un texto de lo compartido entre idiomas que se puede traducir (`POST /panel/traducibles`). */
export type TextoTraducible = { ruta: string; etiqueta: string; original: string; largo: boolean }

export const useTraducibles = (coleccion: string, datos: Record<string, unknown>, activo: boolean) =>
  useQuery({
    queryKey: ['traducibles', coleccion, datos],
    queryFn: () => pedir<{ textos: TextoTraducible[] }>('/panel/traducibles', { metodo: 'POST', cuerpo: { coleccion, datos } }),
    enabled: activo,
    placeholderData: (anterior) => anterior,
    staleTime: 30_000,
  })

export const useComponentesEnEspanol = (sitio: null | number, activo: boolean) =>
  useQuery({
    queryKey: ['idiomas-componentes', sitio],
    queryFn: () => pedir<ComponentesEnEspanol>(`/panel/idiomas/componentes${consulta({ sitio: sitio ?? 0 })}`),
    enabled: sitio !== null && activo,
    staleTime: 60_000,
    // Quien no ve el diseño recibe un 404: para él no hay nada que enseñar.
    retry: false,
  })

export const useVersiones = (sitio: null | number, coleccion: string, id: null | number) =>
  useQuery({
    queryKey: ['versiones-en-idiomas', sitio, coleccion, id],
    queryFn: () => pedir<VersionesDeUnDocumento>(`/panel/traducciones${consulta({ sitio: sitio ?? 0, coleccion, id: id ?? 0 })}`),
    enabled: sitio !== null && id !== null && COLECCIONES_POR_IDIOMA.has(coleccion),
  })

/** El idioma de un listado o de un documento nuevo, de la dirección (`?idioma=en`): el principal si no dice uno que la web tenga. */
export const idiomaDeLaDireccion = (valor: null | string, encendidos: readonly string[] | undefined): CodigoDeIdioma =>
  valor && encendidos?.includes(valor) && (valor === 'en' || valor === 'pt') ? valor : 'es'
